// Lifecycle transition matrix for the Contact cockpit (design handoff
// — design_handoff_contact_cockpit/README.md "Lifecycle transition
// matrix"). Forward-only mostly, with a few legal back-edges:
//   - dormant / disqualified can re-engage to in_sequence
// The cockpit's 11 stages are the canonical lifecycle vocabulary
// (post-LT-358); see types/contact.ts.
//
// Anything not in this table is rejected by the cockpit: clicks on
// non-reachable ribbon segments surface an invalid-transition toast
// rather than silently no-op'ing.

import type { LifecycleStage } from '../../../types/contact';
import type { LifecycleRibbonStage } from '../../../ui';
import { LIFECYCLE_STAGE_LABELS } from '../../../types/contact';

export const COCKPIT_LIFECYCLE_ORDER: readonly LifecycleStage[] = [
  'cold',
  'in_sequence',
  'no_answer',
  'engaged',
  'nurture',
  'callback_scheduled',
  'qualified',
  'opportunity_open',
  'customer',
  'disqualified',
  'dormant',
] as const;

export const LIFECYCLE_TRANSITIONS: Record<LifecycleStage, readonly LifecycleStage[]> = {
  cold: ['in_sequence', 'disqualified'],
  in_sequence: [
    'no_answer',
    'engaged',
    'nurture',
    'callback_scheduled',
    'qualified',
    'disqualified',
  ],
  no_answer: ['in_sequence', 'engaged', 'nurture', 'disqualified', 'dormant'],
  engaged: ['nurture', 'callback_scheduled', 'qualified', 'disqualified'],
  nurture: [
    'in_sequence',
    'engaged',
    'callback_scheduled',
    'qualified',
    'disqualified',
    'dormant',
  ],
  callback_scheduled: ['engaged', 'qualified', 'disqualified', 'no_answer'],
  qualified: ['opportunity_open', 'disqualified'],
  opportunity_open: ['customer', 'disqualified'],
  customer: [],
  disqualified: ['in_sequence'],
  dormant: ['in_sequence'],
  // `contacted` is a legacy Pass-0 stage retained in the LifecycleStage
  // union for back-compat with older contacts. The cockpit treats it as
  // a parking stage that can move forward into in_sequence or sideways
  // to disqualified — same shape as `cold` so the ribbon never strands
  // an older contact.
  contacted: ['in_sequence', 'disqualified'],
};

const TERMINAL_KIND: Partial<Record<LifecycleStage, 'good' | 'bad'>> = {
  customer: 'good',
  disqualified: 'bad',
  dormant: 'bad',
};

export const COCKPIT_RIBBON_STAGES: readonly LifecycleRibbonStage[] =
  COCKPIT_LIFECYCLE_ORDER.map((id) => {
    const stage: LifecycleRibbonStage = {
      id,
      label: LIFECYCLE_STAGE_LABELS[id],
    };
    const terminal = TERMINAL_KIND[id];
    if (terminal) stage.terminal = terminal;
    return stage;
  });

export function getReachableTargets(
  from: LifecycleStage,
): readonly LifecycleStage[] {
  return LIFECYCLE_TRANSITIONS[from] ?? [];
}

export function isValidTransition(
  from: LifecycleStage,
  to: LifecycleStage,
): boolean {
  if (from === to) return false;
  return getReachableTargets(from).includes(to);
}
